/**
 * Auth queries (TanStack Query).
 *
 * - useMe   → current user + company + subscription
 * - useLogin → login mutation
 * - useLogout → logout mutation
 * - useRegister → register mutation
 * - useForgotPassword → password reset email isteği
 * - useResetPassword → token + yeni şifre ile reset
 * - useVerifyEmail → email doğrulama
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

import type { Role, SubscriptionPlan, SubscriptionStatus } from '@shared/enums'

export type MeResponse = {
  user: {
    id: string
    email: string
    fullName: string
    role: Role
    emailVerifiedAt: string | null
    avatarUrl: string | null
  }
  company: {
    id: string
    name: string
    type: 'BUYER' | 'SUPPLIER'
    cityId: number
    logoUrl: string | null
  } | null
  subscription: {
    plan: SubscriptionPlan
    status: SubscriptionStatus
    trialEndsAt: string | null
    expiresAt: string | null
    hasPremiumAccess: boolean
    accessEndsAt: string | null
  } | null
  usage: {
    dailyOffersSubmitted: number
    monthlyOffersSubmitted: number
    dailyLimit: number
    monthlyLimit: number
  } | null
}

export const authKeys = {
  me: ['auth', 'me'] as const,
}

export function useMe() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: async () => {
      const res = await api<{ ok: true; data: MeResponse }>('/auth/me')
      return res.data
    },
    retry: false,
    staleTime: 60_000,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const res = await api<{
        ok: true
        data: { user: { role: Role; fullName: string }; expiresAt: string }
      }>('/auth/login', { method: 'POST', body: input })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: authKeys.me })
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      company: {
        name: string
        taxNumber: string
        type: 'BUYER' | 'SUPPLIER'
        cityId: number
        district?: string
        address?: string
        phone?: string
      }
      user: { email: string; password: string; fullName: string }
    }) => {
      const res = await api<{
        ok: true
        data: { user: { role: Role; fullName: string }; expiresAt: string }
      }>('/auth/register', { method: 'POST', body: input })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: authKeys.me })
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api('/auth/logout', { method: 'POST' })
    },
    onSuccess: () => {
      qc.setQueryData(authKeys.me, null)
      qc.clear()
    },
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (input: { email: string }) => {
      const res = await api<{ ok: true; data: { message: string } }>('/auth/forgot-password', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (input: { token: string; newPassword: string }) => {
      const res = await api<{ ok: true; data: { message: string } }>('/auth/reset-password', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
  })
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (input: { token: string }) => {
      const res = await api<{ ok: true; data: { message: string } }>('/auth/verify-email', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
  })
}
